import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import { FrequencyType, CronConfig } from '../../../../types';

interface NextTriggerPreviewProps {
    frequency: FrequencyType;
    cronConfig: CronConfig;
    previewCount?: number;
}

const calculateNextTriggers = (
    frequency: FrequencyType,
    cronConfig: CronConfig,
    count: number
): moment.Moment[] => {
    const triggers: moment.Moment[] = [];
    const timezone = cronConfig.timezone || moment.tz.guess() || 'UTC';

    // Get current time in the specified timezone
    const now = moment.tz(timezone);

    // Create initial time for today at the specified hour:minute in the target timezone
    let current = moment.tz(timezone)
        .hour(cronConfig.hour)
        .minute(cronConfig.minute)
        .second(0)
        .millisecond(0);

    switch (frequency) {
        case 'daily':
            // For daily, if today's time has passed, start from tomorrow
            if (current.isSameOrBefore(now)) {
                current.add(1, 'day');
            }
            for (let i = 0; i < count; i++) {
                triggers.push(current.clone());
                current.add(1, 'day');
            }
            break;

        case 'weekly':
            // Find the next occurrence of the selected day of week
            const targetDayOfWeek = cronConfig.day;
            const currentDayOfWeek = current.day();
            let daysUntilTarget = (targetDayOfWeek - currentDayOfWeek + 7) % 7;

            // If same day but time passed, move to next week
            if (daysUntilTarget === 0 && current.isSameOrBefore(now)) {
                daysUntilTarget = 7;
            }

            current.add(daysUntilTarget, 'days');

            for (let i = 0; i < count; i++) {
                triggers.push(current.clone());
                current.add(7, 'days');
            }
            break;

        case 'biweekly':
            // Similar to weekly, but every 2 weeks
            const biweeklyTargetDay = cronConfig.day;
            const biweeklyCurrentDay = current.day();
            let biweeklyDaysUntil = (biweeklyTargetDay - biweeklyCurrentDay + 7) % 7;

            if (biweeklyDaysUntil === 0 && current.isSameOrBefore(now)) {
                biweeklyDaysUntil = 14;
            }

            current.add(biweeklyDaysUntil, 'days');

            for (let i = 0; i < count; i++) {
                triggers.push(current.clone());
                current.add(14, 'days');
            }
            break;

        case 'monthly':
            // Find the next occurrence of the selected day of month
            const targetDayOfMonth = cronConfig.day;

            // Set to target day of current month
            const setToTargetDay = (m: moment.Moment, day: number): moment.Moment => {
                const daysInMonth = m.daysInMonth();
                const validDay = Math.min(day, daysInMonth);
                return m.date(validDay);
            };

            current = setToTargetDay(current, targetDayOfMonth);

            // If this month's date has passed, move to next month
            if (current.isSameOrBefore(now)) {
                current.add(1, 'month');
                current = setToTargetDay(current, targetDayOfMonth);
            }

            for (let i = 0; i < count; i++) {
                triggers.push(current.clone());
                current.add(1, 'month');
                current = setToTargetDay(current, targetDayOfMonth);
            }
            break;

        default:
            break;
    }

    return triggers;
};

const NextTriggerPreview: React.FC<NextTriggerPreviewProps> = ({
    frequency,
    cronConfig,
    previewCount = 7,
}) => {
    const timezone = cronConfig.timezone || moment.tz.guess() || 'UTC';

    const nextTriggers = useMemo(() => {
        return calculateNextTriggers(frequency, cronConfig, previewCount);
    }, [frequency, cronConfig, previewCount]);

    if (frequency === 'immediate' || nextTriggers.length === 0) {
        return null;
    }

    const getRelativeTime = (triggerMoment: moment.Moment): string => {
        const now = moment.tz(timezone);
        const diffMs = triggerMoment.diff(now);
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            if (diffHours <= 1) return 'In about an hour';
            return `In ${diffHours} hours`;
        }
        if (diffDays === 1) return 'Tomorrow';
        if (diffDays < 7) return `In ${diffDays} days`;
        if (diffDays < 14) return 'Next week';
        return `In ${Math.floor(diffDays / 7)} weeks`;
    };

    // Get timezone abbreviation
    const tzAbbr = moment.tz(timezone).format('z');

    return (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-3">
                <svg
                    className="w-4 h-4 text-primary-light dark:text-primary-dark"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                </svg>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Next {previewCount} Scheduled Triggers
                </span>
            </div>

            <div className="grid grid-cols-1 gap-2 max-h-[240px] overflow-y-auto pr-2">
                {nextTriggers.map((trigger, index) => (
                    <div
                        key={index}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all ${index === 0
                                ? 'bg-gradient-to-r from-primary-light/10 to-primary-light/5 dark:from-primary-dark/20 dark:to-primary-dark/10 border border-primary-light/20 dark:border-primary-dark/30'
                                : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                    >
                        <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${index === 0
                                    ? 'bg-primary-light text-white dark:bg-primary-dark'
                                    : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                                }`}>
                                {index + 1}
                            </div>
                            <span className={`text-sm ${index === 0
                                    ? 'font-medium text-gray-900 dark:text-white'
                                    : 'text-gray-700 dark:text-gray-300'
                                }`}>
                                {trigger.format('ddd, MMM D, YYYY [at] h:mm A')} {tzAbbr}
                            </span>
                        </div>
                        {index === 0 && (
                            <span className="text-xs px-2 py-1 rounded-full bg-primary-light/20 text-primary-light dark:bg-primary-dark/20 dark:text-primary-dark font-medium">
                                {getRelativeTime(trigger)}
                            </span>
                        )}
                    </div>
                ))}
            </div>

            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                All times shown in {timezone.replace(/_/g, ' ')} ({tzAbbr})
            </p>
        </div>
    );
};

export default NextTriggerPreview;
